/**
 * The reminder email switch, moved from the old organization page.
 *
 * The Configuration stage listed "Reminder email on" as a requirement and gave
 * no way to meet it; the only switch was on the page this replaces. Off is the
 * default for a new organization, and every scheduled reminder is gated on it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../lib/callable';
import page from '../../../shared/Page.module.css';
import { GroupCard } from '../../../shared/GroupCard';

export function RemindersSwitch({ orgId, on }: { orgId: string; on: boolean }) {
  const queryClient = useQueryClient();
  const set = useMutation({
    mutationFn: (enabled: boolean) =>
      call<{ orgId: string; enabled: boolean }, unknown>('sysAdminSetOrgReminders', { orgId, enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding-activity', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['console-orgs'] });
    },
  });

  return (
    <GroupCard id="reminders-title" title="Reminder email">
      {set.error && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          {isCallFailure(set.error) ? set.error.message : 'Could not change that setting.'}
        </div>
      )}
      <p className={page.itemMeta} style={{ maxWidth: '68ch' }}>
        {on ? (
          <>Reminder email is <strong>on</strong>. Staff are reminded about acknowledgements
            that fall due, review dates and stalled approvals.</>
        ) : (
          <>Reminder email is <strong>off</strong>, the default for a new organization. Nobody
            is reminded about anything: acknowledgements go overdue quietly, which looks to
            the customer like the product not working.</>
        )}
      </p>
      <div className={page.actions} style={{ marginTop: 'var(--sp-3)' }}>
        <button type="button" id="reminders-toggle" className={`${page.btn} ${on ? '' : page.btnPrimary}`}
          disabled={set.isPending} onClick={() => set.mutate(!on)}>
          {set.isPending ? 'Saving…' : on ? 'Turn reminders off' : 'Turn reminders on'}
        </button>
      </div>
    </GroupCard>
  );
}
